import { useEffect, useRef, useState } from "react";
import { ClipboardPaste, Loader2 } from "lucide-react";

// Multiple PasteZones can be mounted at once (one per sub-item). To avoid a pasted
// image being uploaded to every zone, only the "active" zone (hovered / focused /
// clicked) handles the paste. Fallback: if nothing is active and a single zone is
// mounted, that zone handles it.
let activeEl = null;
let mountedCount = 0;

export default function PasteZone({ onImage, busy = false, className = "" }) {
  const ref = useRef(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    mountedCount += 1;
    return () => {
      mountedCount -= 1;
      if (activeEl === ref.current) activeEl = null;
    };
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const el = ref.current;
      if (!el) return;
      const isActive = activeEl === el || (activeEl == null && mountedCount === 1);
      if (!isActive) return;
      const items = e.clipboardData?.items || [];
      for (const it of items) {
        if (it.type && it.type.startsWith("image/")) {
          const blob = it.getAsFile();
          if (blob) {
            const ext = (it.type.split("/")[1] || "png").replace("jpeg", "jpg");
            const file = new File([blob], `capture-${Date.now()}.${ext}`, { type: it.type });
            onImage(file);
            e.preventDefault();
          }
          return;
        }
      }
    };
    window.addEventListener("paste", handler);
    return () => window.removeEventListener("paste", handler);
  }, [onImage]);

  const activate = () => { activeEl = ref.current; setActive(true); };
  const onLeave = () => {
    if (document.activeElement !== ref.current) {
      if (activeEl === ref.current) activeEl = null;
      setActive(false);
    }
  };
  const onBlur = () => { if (activeEl === ref.current) activeEl = null; setActive(false); };

  return (
    <div ref={ref} tabIndex={0} data-testid="paste-zone"
      onMouseEnter={activate} onMouseLeave={onLeave} onFocus={activate} onBlur={onBlur} onClick={activate}
      className={`flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed px-3 py-2.5 text-xs outline-none transition-colors ${active ? "border-cyan-400 bg-cyan-500/10 text-cyan-200" : "border-cyan-500/25 bg-slate-950/30 text-slate-400"} ${className}`}>
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin text-cyan-400" /> : <ClipboardPaste className="h-3.5 w-3.5 text-cyan-500/70" />}
      {active ? "Prêt — collez ici" : "Survolez / cliquez puis collez"}
      <span className="font-mono-data text-[10px] text-slate-500">(Ctrl / Cmd + V)</span>
    </div>
  );
}
